// API configuration utility
// This uses the monorepo's config system for consistency

import getConfig from 'next/config';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() || {};

// Get the config from runtime config or fall back to process.env for development
const getRuntimeConfig = () => {
    // Server-side has access to full config
    if (typeof window === 'undefined' && serverRuntimeConfig?.niioConfig) {
        return serverRuntimeConfig.niioConfig;
    }

    // Client-side only has access to public config
    if (publicRuntimeConfig?.niioConfig) {
        return publicRuntimeConfig.niioConfig;
    }

    // Fallback for development/local testing
    return {
        front: {
            url:
                process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3000',
        },
        app: {
            extauth: {
                provider: {
                    google: {
                        webClientId: process.env.NEXT_PUBLIC_GOOGLE_ID || '',
                    },
                },
            },
        },
    };
};

export function getApiBaseUrl(): string {
    const config = getRuntimeConfig();
    const isServer = typeof window === 'undefined';

    // Only log in development mode and when explicitly debugging
    const shouldLog =
        process.env.NODE_ENV === 'development' &&
        process.env.DEBUG_API_CONFIG === 'true';

    if (shouldLog) {
        console.log(`[PAIR-APP] [${isServer ? 'SERVER' : 'CLIENT'}] Config:`, {
            config,
            env: config?.env || 'development',
        });
    }

    // Priority order:
    // 1. Config.server (direct server URL from environment)
    // 2. Config.niio_api_url (API URL from environment)
    // 3. Config.niio_url + /Server (main app URL from environment)
    // 4. Config front values (for backwards compatibility)
    // 5. Environment variables (for local development)
    // 6. Fallback

    if (config?.server) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using config.server:`,
                config.server
            );
        }
        return config.server;
    }

    if (config?.niio_api_url) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using config.niio_api_url:`,
                config.niio_api_url
            );
        }
        return config.niio_api_url;
    }

    if (config?.niio_url) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using config.niio_url + /Server:`,
                config.niio_url
            );
        }
        return config.niio_url + '/Server';
    }

    if (config?.front?.server) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using config.front.server:`,
                config.front.server
            );
        }
        return config.front.server;
    }

    if (config?.front?.niioApiUrl) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using config.front.niioApiUrl:`,
                config.front.niioApiUrl
            );
        }
        return config.front.niioApiUrl;
    }

    if (config?.front?.url) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using config.front.url:`,
                config.front.url
            );
        }
        return config.front.url + '/Server';
    }

    // Fallback to environment variables for local development
    const server = process.env.NEXT_PUBLIC_SERVER || process.env.SERVER;
    const niioApiUrl =
        process.env.NEXT_PUBLIC_NIIO_API_URL || process.env.NIIO_API_URL;
    const niioUrl =
        process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NIIO_URL;

    if (server) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using SERVER env variable:`,
                server
            );
        }
        return server;
    }

    if (niioApiUrl) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using NIIO_API_URL env variable:`,
                niioApiUrl
            );
        }
        return niioApiUrl;
    }

    if (niioUrl) {
        if (shouldLog) {
            console.log(
                `[PAIR-APP] [${
                    isServer ? 'SERVER' : 'CLIENT'
                }] Using NIIO_URL env variable:`,
                niioUrl
            );
        }
        return niioUrl;
    }

    if (shouldLog) {
        console.warn(
            `[PAIR-APP] [${
                isServer ? 'SERVER' : 'CLIENT'
            }] No API base URL configured, using fallback`
        );
    }
    return 'http://localhost:3000/Server';
}

// Export Google Client ID
export function getGoogleClientId(): string {
    const config = getRuntimeConfig();

    // PRIORITY 1: Check config for V2 credentials first (if available)
    if (config?.google_id_v2) {
        console.log(
            '[PAIR-APP] ✅ Using V2 Google Client ID from config.google_id_v2'
        );
        console.log(
            '[PAIR-APP] V2 Client ID starts with:',
            config.google_id_v2.substring(0, 20) + '...'
        );
        return config.google_id_v2;
    }

    // PRIORITY 2: Check environment variable for V2 directly
    const envV2 =
        process.env.GOOGLE_ID_V2 || process.env.NEXT_PUBLIC_GOOGLE_ID_V2;
    if (envV2) {
        console.log(
            '[PAIR-APP] ✅ Using V2 Google Client ID from environment variable'
        );
        console.log(
            '[PAIR-APP] V2 Client ID starts with:',
            envV2.substring(0, 20) + '...'
        );
        return envV2;
    }

    // PRIORITY 3: Try direct config (from environment variable)
    if (config?.google_id) {
        // Check if this is actually a V2 client ID (starts with 629091735397 for your V2 client)
        const isV2 = config.google_id.startsWith('629091735397');
        console.log(
            `[PAIR-APP] ${isV2 ? '✅' : '⚠️'} Using Google ${
                isV2 ? 'V2' : 'V1'
            } Client ID from config.google_id`
        );
        console.log(
            '[PAIR-APP] Client ID starts with:',
            config.google_id.substring(0, 20) + '...'
        );
        return config.google_id;
    }

    // Try app config
    if (config?.app?.extauth?.provider?.google?.webClientId) {
        return config.app.extauth.provider.google.webClientId;
    }

    // Try front config (alternative location)
    if (config?.front?.googleId) {
        return config.front.googleId;
    }

    // Fallback to environment variable for local development
    const envGoogleId =
        process.env.NEXT_PUBLIC_GOOGLE_ID || process.env.GOOGLE_ID;
    if (envGoogleId) {
        return envGoogleId;
    }

    return '';
}

// Get Google OAuth Redirect URI
export function getGoogleRedirectUri(): string {
    // For the pair app with @react-oauth/google library in popup mode,
    // the library forces 'postmessage' as the redirect_uri regardless of what we specify
    // The pair app is always on a different subdomain than the API server:
    // - Production: pair.niio.com → www.niio.com/Server
    // - Dev/Staging: pair.<branch>.niio.dev → app.<branch>.niio.dev/Server
    // - Local: pair.shaisnir.local.niio.dev → app.shaisnir.local.niio.dev/Server

    // Only log during actual OAuth flow (when explicitly debugging)
    const shouldLog =
        process.env.NODE_ENV === 'development' &&
        process.env.DEBUG_API_CONFIG === 'true';

    if (shouldLog) {
        if (typeof window !== 'undefined') {
            console.log(
                '[PAIR-APP] Using postmessage for cross-domain OAuth (required by @react-oauth/google in popup mode)'
            );
            console.log('[PAIR-APP] Current Origin:', window.location.origin);
            console.log('[PAIR-APP] API Server:', getApiBaseUrl());
        } else {
            console.log(
                '[PAIR-APP] [SERVER] Using postmessage for cross-domain OAuth'
            );
        }
    }

    // The @react-oauth/google library in popup mode ignores our redirect_uri and uses 'postmessage'
    // So we return 'postmessage' to be consistent
    return 'postmessage';
}
